import { ErrorCode, PlanLimit, PlanTier, type PlanLimitReachedData } from '@fluenceur/types';

/**
 * The 402 codes that name a limit of the brand's plan rather than a lapsed
 * subscription (including an email from the brand's mailbox to a masked prospect). `E_PLAN_LIMIT_REACHED` (legacy generic limits) is not one of them:
 * it keeps the subscription branch it always had.
 */
const LIMIT_OF_CODE: ReadonlyMap<string, PlanLimit> = new Map([
  [ErrorCode.E_PLAN_DISCOVERY_QUOTA_REACHED, PlanLimit.DISCOVERY_SEARCHES],
  [ErrorCode.E_PLAN_BRIEF_LIMIT_REACHED, PlanLimit.BRIEFS],
  [ErrorCode.E_PLAN_PROGRAM_LIMIT_REACHED, PlanLimit.ACTIVE_PROGRAMS],
  [ErrorCode.E_PLAN_FLOWS_LOCKED, PlanLimit.FLOWS],
  [ErrorCode.E_PLAN_FLOW_LIMIT_REACHED, PlanLimit.FLOWS],
  [ErrorCode.E_PLAN_TEAM_MEMBERS_LOCKED, PlanLimit.TEAM_MEMBERS],
  [ErrorCode.E_PLAN_MCP_LOCKED, PlanLimit.MCP],
  [ErrorCode.E_PLAN_RECOMMENDATIONS_QUOTA_REACHED, PlanLimit.RECOMMENDATIONS],
  [ErrorCode.E_PLAN_CREATOR_CONTACT_LOCKED, PlanLimit.CREATOR_CONTACT],
  [ErrorCode.E_PLAN_PROFILE_UNLOCKS_QUOTA_REACHED, PlanLimit.PROFILE_UNLOCKS],
  [ErrorCode.E_PLAN_GIFTING_INVITATIONS_QUOTA_REACHED, PlanLimit.GIFTING_INVITATIONS],
]);

const LIMITS: ReadonlySet<string> = new Set(Object.values(PlanLimit));
const TIERS: ReadonlySet<string> = new Set(Object.values(PlanTier));

type ApiBody = { code?: unknown; data?: unknown } | null | undefined;

/** Whether a 402 body carries one of the plan-limit codes, readable or not. */
export function isPlanLimitCode(body: ApiBody): boolean {
  return typeof body?.code === 'string' && LIMIT_OF_CODE.has(body.code);
}

/**
 * The limit a plan-limit code names, read from the code alone. The fallback
 * when the payload cannot be read: the upsell then takes the tiers from the
 * plan the dashboard knows the brand is on.
 */
export function planLimitOfCode(body: ApiBody): PlanLimit | null {
  return typeof body?.code === 'string' ? (LIMIT_OF_CODE.get(body.code) ?? null) : null;
}

/**
 * The limit a plan-limit 402 names, with its tiers and counts, or null when
 * the body is not one or its payload cannot be read (see `planLimitOfCode`).
 */
export function readPlanLimitRefusal(body: ApiBody): PlanLimitReachedData | null {
  if (!isPlanLimitCode(body)) return null;

  const data = body?.data as Partial<PlanLimitReachedData> | null | undefined;
  if (!data || typeof data !== 'object') return null;
  if (typeof data.limit !== 'string' || !LIMITS.has(data.limit)) return null;
  if (typeof data.currentTier !== 'string' || !TIERS.has(data.currentTier)) return null;
  if (typeof data.targetTier !== 'string' || !TIERS.has(data.targetTier)) return null;

  return {
    limit: data.limit,
    currentTier: data.currentTier,
    targetTier: data.targetTier,
    ...(typeof data.used === 'number' && { used: data.used }),
    ...(typeof data.max === 'number' && { max: data.max }),
    ...(typeof data.resetsAt === 'string' && { resetsAt: data.resetsAt }),
  };
}

/** Whether an axios error is a plan-limit refusal, which the upsell already answers. */
export function isPlanLimitError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: ApiBody } } | null)?.response;
  return response?.status === 402 && isPlanLimitCode(response.data);
}
